import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, urlPoza } from "./api";
import { statusExpirare, textStare, formatData } from "../utils/expirare";
import {
  ETICHETE,
  stareMasina,
  numaraDocumente,
  filtreazaMasini,
  SORTARI,
} from "../utils/flota";

const FILTRE = [
  { id: "toate", text: "Toate" },
  { id: "probleme", text: "Au nevoie de atenție" },
  { id: "expirate", text: "Expirate" },
  { id: "ok", text: "În regulă" },
  { id: "service", text: "În service" },
];

const OPT_SORT = [
  { id: "urgenta", text: "După urgență" },
  { id: "numar", text: "După număr" },
  { id: "model", text: "După model" },
];

export default function Masini() {
  const navigate = useNavigate();
  const [masini, setMasini] = useState([]);
  const [stare, setStare] = useState("loading"); // loading | ok | error
  const [cautare, setCautare] = useState("");
  const [filtru, setFiltru] = useState("toate");
  const [sortare, setSortare] = useState("urgenta");

  useEffect(() => {
    apiFetch("/masini")
      .then((data) => {
        setMasini(data.masini || []);
        setStare("ok");
      })
      .catch(() => setStare("error"));
  }, []);

  const afisate = useMemo(() => {
    const f = filtreazaMasini(masini, { cautare, filtru });
    return [...f].sort(SORTARI[sortare]);
  }, [masini, cautare, filtru, sortare]);

  return (
    <div className="masini">
      <div className="dash__head">
        <h1>Mașinile mele</h1>
        <p>Starea documentelor pentru fiecare mașină din flotă.</p>
      </div>

      {stare === "loading" && <SkeletonMasini />}

      {stare === "error" && (
        <div className="portal__loading">
          Nu am putut încărca mașinile. Reîncearcă mai târziu.
        </div>
      )}

      {stare === "ok" && masini.length === 0 && (
        <div className="portal__loading">
          Nu există mașini asociate contului tău încă.
        </div>
      )}

      {stare === "ok" && masini.length > 0 && (
        <>
          <div className="toolbar">
            <div className="toolbar__search">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.3-4.3" />
              </svg>
              <input
                type="text"
                value={cautare}
                onChange={(e) => setCautare(e.target.value)}
                placeholder="Caută după număr, model sau șasiu…"
              />
              {cautare && (
                <button className="toolbar__clear" onClick={() => setCautare("")} aria-label="Șterge căutarea">×</button>
              )}
            </div>

            <div className="toolbar__sort">
              <label>Sortează</label>
              <select value={sortare} onChange={(e) => setSortare(e.target.value)}>
                {OPT_SORT.map((o) => (
                  <option key={o.id} value={o.id}>{o.text}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="chips">
            {FILTRE.map((f) => (
              <button
                key={f.id}
                className={`chip ${filtru === f.id ? "chip--active" : ""}`}
                onClick={() => setFiltru(f.id)}
              >
                {f.text}
              </button>
            ))}
          </div>

          <div className="legenda">
            <span><span className="dot dot--verde" /> Valabil</span>
            <span><span className="dot dot--galben" /> Expiră curând</span>
            <span><span className="dot dot--rosu" /> Expirat</span>
            <span><span className="dot dot--necunoscut" /> Fără dată încărcată</span>
          </div>

          <p className="masini__count">
            {afisate.length}{" "}
            {afisate.length === 1 ? "mașină" : "mașini"}
            {filtru !== "toate" || cautare ? ` (din ${masini.length})` : ""}
          </p>

          {afisate.length === 0 ? (
            <div className="portal__loading">
              Nicio mașină nu corespunde căutării.
            </div>
          ) : (
            <div className="masini__grid" key={`${filtru}-${sortare}-${cautare ? "q" : ""}`}>
              {afisate.map((m) => (
                <CardMasina key={m.id} masina={m} onClick={() => navigate(`/portal/masini/${m.id}`)} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CardMasina({ masina, onClick }) {
  const sm = stareMasina(masina);
  const cnt = numaraDocumente(masina);
  const problematice = cnt.rosu + cnt.galben;

  return (
    <article
      className={`masina masina--clic masina--${sm.stare}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
    >
      <div className="masina__head">
        <div className="masina__ident">
          <PozaMasina poza={masina.poza} model={masina.model} />
          <div>
            <h3>{masina.nrInmatriculare || "—"}</h3>
            <span className="masina__model">{masina.model || "Model nespecificat"}</span>
          </div>
        </div>
        <div className="masina__pills">
          {masina.statusOperational === "in_service" && (
            <span className="pill pill--service">🔧 În service</span>
          )}
          <StatusPill stare={sm.stare} zile={sm.zile} problematice={problematice} />
        </div>
      </div>

      <div className="masina__docs">
        {Object.entries(masina.expira).map(([tip, data]) => {
          const s = statusExpirare(data);
          return (
            <div className={`doc doc--${s.stare}`} key={tip}>
              <div className="doc__top">
                <span className={`dot dot--${s.stare}`} />
                <span className="doc__nume">{ETICHETE[tip]}</span>
              </div>
              <span className="doc__data">{formatData(data)}</span>
              <span className="doc__stare">{textStare(s.zile)}</span>
            </div>
          );
        })}
      </div>

      <span className="masina__detalii">
        Vezi detalii
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 6l6 6-6 6" />
        </svg>
      </span>
    </article>
  );
}

function PozaMasina({ poza, model }) {
  const [eroare, setEroare] = React.useState(false);
  const src = urlPoza(poza);
  if (!src || eroare) {
    return (
      <span className="masina__poza masina__poza--gol" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 11l1.5-4.5A2 2 0 018.4 5h7.2a2 2 0 011.9 1.5L19 11" />
          <path d="M3 11h18v5a1 1 0 01-1 1h-1a1 1 0 01-1-1v-1H6v1a1 1 0 01-1 1H4a1 1 0 01-1-1v-5z" />
          <circle cx="7" cy="14" r="1" /><circle cx="17" cy="14" r="1" />
        </svg>
      </span>
    );
  }
  return (
    <img
      className="masina__poza"
      src={src}
      alt={model || "Mașină"}
      loading="lazy"
      onError={() => setEroare(true)}
    />
  );
}

function StatusPill({ stare, zile, problematice }) {
  if (stare === "rosu")
    return <span className="pill pill--rosu">{problematice > 1 ? `${problematice} probleme` : "Document expirat"}</span>;
  if (stare === "galben")
    return <span className="pill pill--galben">{textStare(zile)}</span>;
  if (stare === "verde")
    return <span className="pill pill--verde">Toate valabile</span>;
  return <span className="pill pill--necunoscut">Date incomplete</span>;
}

function SkeletonMasini() {
  return (
    <div className="masini__grid" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div className="masina masina--skel" key={i}>
          <div className="masina__head">
            <div style={{ flex: 1 }}>
              <span className="skel skel--line" style={{ width: "45%", height: 22 }} />
              <span className="skel skel--line" style={{ width: "60%", height: 14, marginTop: 8 }} />
            </div>
            <span className="skel skel--pill" />
          </div>
          <div className="masina__docs">
            {[0, 1, 2, 3].map((j) => (
              <div className="doc" key={j}>
                <span className="skel skel--line" style={{ width: "55%", height: 12 }} />
                <span className="skel skel--line" style={{ width: "80%", height: 16, marginTop: 8 }} />
                <span className="skel skel--line" style={{ width: "70%", height: 10, marginTop: 6 }} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
